import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardBody,
  TabPane,
  TabContent,
  Col,
} from 'reactstrap';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import NavTab from '../../shared/components/custom/NavTab';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import t, { partial } from '../../util/translation/translation';
import TransactionsListPage from '../transactions/TransactionsListPage';
import SubscriptionPlanForm from './components/SubscriptionPlanForm';
import moment from 'moment';
import GamesListPage from './components/GamesListPage';
import LeaderboardPage from './components/LeaderboardPage';
import PlanManagersPage from './components/PlanManagersPage';
import TeamSlugPage from './components/TeamSlugPage';
import GoalSponsorContactFormPage from './GoalSponsorContactFormPage';
import SubscriptionListPage from './SubscriptionListPage';
import useConfig from '../../util/useConfig';

const SubscriptionPlanFormPage = () => {
  const { subscriptionPlanID } = useParams();
  const editMode = subscriptionPlanID !== undefined;
  const config = useConfig();

  const [tab, setTab] = useState('0');
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionPrizes, setSubscriptionPrizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubscriptionPrizes, setLoadingSubscriptionPrizes] =
    useState(true);
  const history = useHistory();

  const p = partial('goalSponsor');
  const s = partial('shared');
  const getSubscriptionPlan = async () => {
    setLoading(true);
    try {
      const resSubscriptionPlan =
        await Api.goalSponsor.subscriptionPlans.getSubscriptionPlanById(
          subscriptionPlanID
        );
      const defaultPaymentOption = await Api.transactions.getPaymentOption(
        resSubscriptionPlan.defaultPaymentOptionId
      );
      setSubscriptionPlan({
        ...resSubscriptionPlan,
        defaultPaymentOption,
      });
      setLoading(false);
    } catch (err) {
      toast.error(JSON.stringify(err));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subscriptionPlanID) {
      getSubscriptionPlan();
    }
    getSubscriptionPrizes();
  }, [subscriptionPlanID]);

  const getSubscriptionPrizes = async () => {
    setLoadingSubscriptionPrizes(true);
    try {
      const resSubscriptionPrizes = await Api.goalSponsor.prizes.getAllPrizes();
      setSubscriptionPrizes(resSubscriptionPrizes);
      setLoadingSubscriptionPrizes(false);
    } catch (err) {
      toast.error(p('prizesRetrievingERROR'));
      setLoadingSubscriptionPrizes(false);
    }
  };

  const onSubscriptionPlanSubmit = async (values) => {
    const payload = {
      name: values.name,
      description: values.description,
      isActive: values.isActive || false,
      image: values.headingImage.croppedBase64 || values.headingImage.preview,
      subtitle: values.subtitle,
      endDate: moment(values.endDate),
      planDesiredValue:
        values.planDesiredValue !== undefined
          ? Number(values.planDesiredValue)
          : 0,
      goalCapPerGame: [
        Number(values.goal1),
        Number(values.goal2),
        Number(values.goal3),
      ],
      showPriceMeter: values.showPriceMeter || false,
      scoreType: values.scoreType?.value || values.scoreType,
      externalInvoiceCompanies: values.externalInvoiceCompanies || false,
      goalCapPerGameStep: Number(values.goalCapPerGameStep) || 1,
      popularAmounts: values.popularAmounts.map(Number),
      amountStep: Number(values.amountStep),
      amountMin: Number(values.amountMin),
      // Team slug/type are edited on their own tab (self-contained, own
      // save) - not part of this form, so carry over whatever's already
      // saved on the plan rather than clearing it on every Details save.
      teamSlug: subscriptionPlan?.teamSlug || null,
      teamType: subscriptionPlan?.teamType || null,
      defaultPaymentOption: {
        currency: config.currencyCaps,
        optionName: t('paymentOptions.subscriptionPaymentForGoalSubscription', {
          entityName: values.name,
        }),
        vat: Number(values.vat.value),
      },
    };

    setLoading(true);
    try {
      let finalPlanId = subscriptionPlanID;
      if (subscriptionPlanID) {
        await Api.goalSponsor.subscriptionPlans.updateSubscriptionPlan(
          subscriptionPlanID,
          payload
        );
      } else {
        const resSubscriptionPlan =
          await Api.goalSponsor.subscriptionPlans.createSubscriptionPlan(
            payload
          );
        finalPlanId = resSubscriptionPlan.id;
      }
      const prizeIds = values?.prizes?.map((prize) => prize.value) || [];
      if (prizeIds.length > 0) {
        await Api.goalSponsor.prizes.setPrizesOnPlan(finalPlanId, {
          prizeIds,
        });
      }
      setLoading(false);
      toast.success(p('subscriptionPlanSaved'));
      if (!editMode) {
        history.replace(
          `/goal-sponsor/subscription-plans/edit/${finalPlanId}?tab=2`
        );
      }
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
    }
  };

  return (
    <Container>
      <Loading loading={loading || loadingSubscriptionPrizes} />
      <Row>
        <Col>
          <Card>
            <CardBody className="p-0">
              <NavTab
                tabs={[
                  { tab: `${s('details')}` },
                  { disabled: !editMode, tab: p('games') },
                  { disabled: !editMode, tab: p('subscriptions') },
                  { disabled: !editMode, tab: s('communication') },
                  { disabled: !editMode, tab: p('leaderboard') },
                  { disabled: !editMode, tab: s('transactions') },
                  { disabled: !editMode, tab: p('managers') },
                  { disabled: !editMode, tab: p('team') },
                ]}
                setTab={setTab}
                activeTab={tab}
                size="sm"
              />
              <TabContent activeTab={tab}>
                <TabPane tabId="1" className="m-3">
                  <SubscriptionPlanForm
                    type={subscriptionPlanID ? 'edit' : 'create'}
                    subscriptionPlan={subscriptionPlan}
                    onSubmit={onSubscriptionPlanSubmit}
                    subscriptionPrizes={subscriptionPrizes}
                  />
                </TabPane>
                {editMode && (
                  <>
                    <TabPane tabId="2">
                      <GamesListPage planId={subscriptionPlanID} />
                    </TabPane>
                    <TabPane tabId="3">
                      <SubscriptionListPage
                        planId={subscriptionPlanID}
                        scoreType={subscriptionPlan?.scoreType}
                      />
                    </TabPane>
                    <TabPane tabId="4" className="m-3">
                      <GoalSponsorContactFormPage
                        subscriptionPlan={subscriptionPlan}
                      />
                    </TabPane>
                    <TabPane tabId="5">
                      <LeaderboardPage planId={subscriptionPlanID} />
                    </TabPane>
                    <TabPane tabId="6">
                      <TransactionsListPage entityId={subscriptionPlanID} />
                    </TabPane>
                    <TabPane tabId="7">
                      <PlanManagersPage planId={subscriptionPlanID} />
                    </TabPane>
                    <TabPane tabId="8">
                      <TeamSlugPage planId={subscriptionPlanID} />
                    </TabPane>
                  </>
                )}
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SubscriptionPlanFormPage;
